import React, { useEffect, useRef, useState, useCallback } from "react";
import { MapPin, X } from "@phosphor-icons/react";
import axios from "axios";

/**
 * LocationGate — silent, Uber/Swiggy-style location collection.
 *
 * Behaviour (NEVER blocks the UI):
 *   1. On mount, silently probe `navigator.permissions.query({ name:'geolocation' })`.
 *   2. If `granted`      → start watching + ping every 30 s. No UI at all.
 *   3. If `prompt`       → show a small dismissible top banner once per
 *                          session with an "Enable location" button. Tapping
 *                          it triggers the browser prompt. Dismissing hides
 *                          the banner for the rest of the session.
 *   4. If `denied` /
 *      unavailable       → silent. Nothing is shown unless the host
 *                          surface explicitly asks for it. The user keeps
 *                          using the app.
 *
 * The ping ticker is only active when permission is `granted`.
 * On revocation we quietly stop the ticker — no modal, no logout.
 */
const PING_INTERVAL_MS = 30_000;
const BACKEND = process.env.REACT_APP_BACKEND_URL;

const SESS_DISMISS_KEY = "rescigo_loc_prompt_dismissed";

const LocationRequired = ({ perm, label, onAllow }) => (
  <div className="min-h-screen bg-[#0F1F0C] text-white flex items-center justify-center p-6" data-testid="location-required">
    <div className="max-w-sm w-full text-center space-y-5">
      <div className="mx-auto w-16 h-16 rounded-full bg-[#2D5A27] flex items-center justify-center"><MapPin size={32} weight="fill" /></div>
      <h1 className="text-xl font-semibold">{label} needs your location</h1>
      {perm === "unknown" ? <p className="text-sm text-white/70" data-testid="location-required-checking">Checking location permission…</p> : <>
        <p className="text-sm text-white/80">Location stays on while you use the app, for dispatch, route tracking and SOS.
          When your phone asks, choose <b>Allow</b> (or <b>Allow while using the app</b>).</p>
        {perm === "denied" && (
          <div className="text-left text-xs bg-white/10 rounded-lg p-3 space-y-1" data-testid="location-required-denied-help">
            <p className="font-semibold">Location is blocked for this app.</p>
            <p>Tap the lock / ⓘ icon next to the web address → <b>Permissions</b> → <b>Location</b> → <b>Allow</b>, then tap the button below.</p>
            <p>On iPhone: Settings → Safari → Location → Allow.</p>
          </div>)}
        {perm === "unavailable" && <p className="text-xs text-[#FCD34D]" data-testid="location-required-gps-off">We couldn't get a location. Please turn on GPS / Location in your phone settings.</p>}
        <button onClick={perm === "denied" ? () => window.location.reload() : onAllow} data-testid="location-required-allow"
          className="w-full h-12 rounded-full bg-white text-[#0F1F0C] font-semibold">
          {perm === "denied" ? "I've allowed it — reload" : "Allow location"}</button>
      </>}
    </div>
  </div>
);

function _getToken(key) {
  try { return localStorage.getItem(key) || ""; } catch { return ""; }
}

export default function LocationGate({
  children,
  pingPath,
  tokenKey = "rescigo_token",
  bodyShape = "universal",
  staffId = null,
  // Kept for API compatibility; no longer used for aggressive logouts.
  onRevoked,                                              // eslint-disable-line no-unused-vars
  label = "Rescigo",
  // Field app: ask on open, no banner, block the UI until location is allowed.
  required = false,
}) {
  const [perm, setPerm] = useState("unknown");            // unknown | granted | prompt | denied | unavailable
  const [coords, setCoords] = useState(null);
  const [banner, setBanner] = useState(false);
  const watchIdRef = useRef(null);
  const tickerRef  = useRef(null);

  // ------------------------------------------------------------
  // Permission probe — SILENT. Uses the Permissions API when
  // available so we don't fire a prompt the user never asked for.
  // ------------------------------------------------------------
  const startWatch = useCallback(() => {
    if (!navigator.geolocation) return;
    if (watchIdRef.current != null) return;              // already watching
    try {
      watchIdRef.current = navigator.geolocation.watchPosition(
        (pos) => {
          setCoords({
            lat: pos.coords.latitude,
            lng: pos.coords.longitude,
            accuracy_m: pos.coords.accuracy || null,
          });
          setPerm("granted");
          setBanner(false);
        },
        (err) => {
          if (err?.code === 1) {
            setPerm("denied");
            try { navigator.geolocation.clearWatch(watchIdRef.current); } catch { /* noop */ }
            watchIdRef.current = null;
          }
          else if (err?.code === 2) setPerm("unavailable");
          // code 3 (timeout) → leave perm as-is, keep retrying silently
        },
        { enableHighAccuracy: true, maximumAge: 15_000, timeout: 20_000 },
      );
    } catch { /* ignore */ }
  }, []);

  useEffect(() => {
    if (typeof navigator === "undefined" || !navigator.geolocation) {
      setPerm("unavailable");
      return;
    }
    let cancelled = false;
    const probe = async () => {
      // Try the Permissions API first — doesn't trigger a prompt.
      if (navigator.permissions?.query) {
        try {
          const s = await navigator.permissions.query({ name: "geolocation" });
          if (cancelled) return;
          setPerm(s.state);
          s.onchange = () => { setPerm(s.state); if (s.state === "granted") startWatch(); };
          if (s.state === "granted") {
            startWatch();
          } else if (required && s.state === "prompt") {
            startWatch();                                  // fires the browser prompt right away
          } else if (s.state === "prompt") {
            // Session-level dismiss honoured.
            try {
              const d = sessionStorage.getItem(SESS_DISMISS_KEY);
              if (!d) setBanner(true);
            } catch { setBanner(true); }
          }
          return;
        } catch { /* fall through */ }
      }
      // Fallback: no Permissions API — try a lightweight getCurrentPosition.
      navigator.geolocation.getCurrentPosition(
        () => { if (!cancelled) { setPerm("granted"); startWatch(); } },
        (err) => {
          if (cancelled) return;
          if (err?.code === 1) setPerm("denied");
          else setPerm("prompt");
          if (required) return;
          try {
            const d = sessionStorage.getItem(SESS_DISMISS_KEY);
            if (err?.code !== 1 && !d) setBanner(true);
          } catch { /* noop */ }
        },
        { timeout: required ? 20_000 : 5_000, maximumAge: 60_000 },
      );
    };
    probe();
    return () => {
      cancelled = true;
      if (watchIdRef.current != null) {
        try { navigator.geolocation.clearWatch(watchIdRef.current); } catch { /* noop */ }
        watchIdRef.current = null;
      }
    };
  }, [startWatch, required]);

  // ------------------------------------------------------------
  // 30-second ping ticker — only while granted.
  // ------------------------------------------------------------
  useEffect(() => {
    if (perm !== "granted" || !coords || !pingPath) return;
    let cancelled = false;
    const sendOne = async () => {
      if (cancelled) return;
      const tok = _getToken(tokenKey);
      if (!tok) return;
      let body;
      if (bodyShape === "staff") {
        body = { staff_id: staffId, latitude: coords.lat,
                 longitude: coords.lng, accuracy_m: coords.accuracy_m };
      } else {
        body = { lat: coords.lat, lng: coords.lng,
                 accuracy_m: coords.accuracy_m };
      }
      try {
        await axios.post(`${BACKEND}${pingPath}`, body, {
          headers: { Authorization: `Bearer ${tok}` },
          timeout: 12_000,
        });
      } catch { /* silent, best-effort */ }
    };
    sendOne();
    tickerRef.current = setInterval(sendOne, PING_INTERVAL_MS);
    return () => {
      cancelled = true;
      if (tickerRef.current) clearInterval(tickerRef.current);
      tickerRef.current = null;
    };
  }, [perm, coords, pingPath, tokenKey, bodyShape, staffId]);

  // ------------------------------------------------------------
  // UI — a tiny inline banner, NEVER a blocker.
  // ------------------------------------------------------------
  const dismiss = () => {
    try { sessionStorage.setItem(SESS_DISMISS_KEY, "1"); } catch { /* noop */ }
    setBanner(false);
  };
  const enable = () => {
    // Trigger the browser prompt by requesting a single fix.
    if (!navigator.geolocation) { setPerm("unavailable"); setBanner(false); return; }
    navigator.geolocation.getCurrentPosition(
      (pos) => {
        setPerm("granted");
        setCoords({
          lat: pos.coords.latitude, lng: pos.coords.longitude,
          accuracy_m: pos.coords.accuracy || null,
        });
        setBanner(false);
        startWatch();
      },
      (err) => {
        if (err?.code === 1) setPerm("denied");
        else if (err?.code === 2) setPerm("unavailable");
        if (!required) dismiss();
      },
      { enableHighAccuracy: true, timeout: 20_000, maximumAge: 0 },
    );
  };

  if (required) {
    const blocked = perm === "denied" || perm === "prompt" || perm === "unknown" || (perm === "unavailable" && !coords);
    if (!blocked) return <>{children}</>;
    return <LocationRequired perm={perm} label={label} onAllow={enable} />;
  }

  return (
    <>
      {banner && perm === "prompt" && (
        <div
          role="status"
          data-testid="location-gate-banner"
          className="w-full bg-[#F0EEE4] border-b border-[#D9D5C6] text-[#2F2D29]
                     px-4 py-2 flex items-center gap-3 text-sm z-30"
        >
          <MapPin size={16} weight="duotone" className="text-[#2D5A27] shrink-0" />
          <div className="flex-1 min-w-0">
            <span className="font-medium">{label}</span>{" "}
            works best with your location — for dispatch, safety and SOS.
          </div>
          <button
            type="button"
            onClick={enable}
            data-testid="location-gate-enable"
            className="h-8 px-3 rounded-md bg-[#2D5A27] text-white text-xs font-medium hover:bg-[#3A7033]"
          >
            Enable location
          </button>
          <button
            type="button"
            onClick={dismiss}
            data-testid="location-gate-dismiss"
            aria-label="Dismiss"
            className="w-7 h-7 rounded hover:bg-[#D9D5C6] flex items-center justify-center"
          >
            <X size={14} />
          </button>
        </div>
      )}
      {children}
    </>
  );
}
